import { NavLink, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Package, Wrench, CalendarClock, FileText,
  ClipboardList, Flame, LogOut, UserCog, Target, Building2, RefreshCcw, Route,
  FileSpreadsheet, Boxes, Tag, Receipt, ShoppingBag, GraduationCap,
} from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { OfflineBar } from "@/components/OfflineBar";
import { Button } from "@/components/ui/button";

const ALL_NAV = [
  { to: "/", label: "Dashboard", icon: LayoutDashboard, roles: ["azienda", "agente"], test: "nav-dashboard" },
  { to: "/agenda", label: "Agenda", icon: Route, roles: ["tecnico", "azienda"], test: "nav-agenda" },
  { to: "/interventi", label: "Interventi", icon: Wrench, roles: ["azienda", "tecnico"], test: "nav-interventi" },
  { to: "/clienti", label: "Clienti", icon: Users, roles: ["azienda", "agente"], test: "nav-clienti" },
  { to: "/impianti", label: "Impianti", icon: Building2, roles: ["azienda", "agente", "tecnico"], test: "nav-impianti" },
  { to: "/manutenzioni", label: "Manutenzioni", icon: CalendarClock, roles: ["azienda"], test: "nav-manutenzioni" },
  { to: "/contratti", label: "Contratti", icon: FileText, roles: ["azienda"], test: "nav-contratti" },
  { to: "/fatture", label: "Fatture", icon: Receipt, roles: ["azienda"], test: "nav-fatture" },
  { to: "/vendite", label: "Vendite dirette", icon: ShoppingBag, roles: ["azienda", "agente"], test: "nav-vendite" },
  { to: "/formazione", label: "Formazione", icon: GraduationCap, roles: ["azienda", "agente"], test: "nav-formazione" },
  { to: "/lead", label: "Lead", icon: Target, roles: ["azienda", "agente"], test: "nav-lead" },
  { to: "/preventivi", label: "Preventivi", icon: FileSpreadsheet, roles: ["azienda", "agente"], test: "nav-preventivi" },
  { to: "/magazzino", label: "Magazzino", icon: Boxes, roles: ["azienda"], test: "nav-magazzino" },
  { to: "/listini", label: "Listini", icon: Tag, roles: ["azienda"], test: "nav-listini" },
  { to: "/prodotti", label: "Prodotti", icon: Package, roles: ["azienda", "agente"], test: "nav-prodotti" },
  { to: "/utenti", label: "Utenti", icon: UserCog, roles: ["azienda"], test: "nav-utenti" },
  { to: "/sync", label: "Business Exp.", icon: RefreshCcw, roles: ["azienda"], test: "nav-sync" },
  { to: "/portale", label: "Il mio impianto", icon: ClipboardList, roles: ["cliente"], test: "nav-portale" },
];

const ROLE_LABEL = { azienda: "Azienda", agente: "Agente", tecnico: "Tecnico", cliente: "Cliente" };

export const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const nav = ALL_NAV.filter((n) => n.roles.includes(user?.role));
  const mobileNav = nav.slice(0, 5);

  const onLogout = async () => {
    await logout();
    navigate("/login");
  };

  return (
    <div className="min-h-screen bg-background flex">
      <aside className="hidden lg:flex w-64 shrink-0 flex-col border-r border-border bg-card">
        <div className="px-6 py-7 border-b border-border">
          <div className="flex items-center gap-2">
            <Flame className="h-6 w-6 text-primary" strokeWidth={2.5} />
            <span className="font-display font-black text-lg tracking-tighter">FIREGUARD</span>
          </div>
          <p className="overline mt-2">Gestionale antincendio</p>
        </div>
        <nav className="flex-1 px-3 py-6 space-y-1 overflow-y-auto">
          {nav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={n.test}
              className={({ isActive }) =>
                `flex items-center gap-3 px-3 py-2.5 text-sm font-medium rounded-sm transition-colors duration-200 ${
                  isActive ? "bg-primary text-primary-foreground" : "text-muted-foreground hover:bg-accent hover:text-foreground"
                }`
              }
            >
              <n.icon className="h-4 w-4" />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t border-border p-4">
          <p className="text-sm font-semibold truncate" data-testid="sidebar-user-name">{user?.name}</p>
          <p className="overline">{ROLE_LABEL[user?.role]}</p>
          <Button
            variant="outline"
            size="sm"
            className="mt-3 w-full rounded-sm"
            onClick={onLogout}
            data-testid="logout-btn"
          >
            <LogOut className="h-4 w-4 mr-2" /> Esci
          </Button>
        </div>
      </aside>

      <div className="flex-1 min-w-0 flex flex-col">
        <OfflineBar />
        <header className="lg:hidden sticky top-0 z-30 flex items-center justify-between px-4 h-14 border-b border-border bg-background/85 backdrop-blur-xl">
          <div className="flex items-center gap-2">
            <Flame className="h-5 w-5 text-primary" strokeWidth={2.5} />
            <span className="font-display font-black tracking-tighter">FIREGUARD</span>
          </div>
          <div className="flex items-center gap-3">
            <span className="overline">{ROLE_LABEL[user?.role]}</span>
            <Button variant="ghost" size="icon" onClick={onLogout} data-testid="logout-btn-mobile">
              <LogOut className="h-5 w-5" />
            </Button>
          </div>
        </header>

        <main className="flex-1 px-4 py-6 lg:px-10 lg:py-10 pb-24 lg:pb-10 max-w-[1500px]">{children}</main>

        <nav className="lg:hidden fixed bottom-0 left-0 right-0 z-30 grid grid-cols-5 border-t border-border bg-background/95 backdrop-blur-xl">
          {mobileNav.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/"}
              data-testid={`${n.test}-mobile`}
              className={({ isActive }) =>
                `flex flex-col items-center justify-center gap-1 py-3 min-h-[56px] text-[10px] font-semibold uppercase tracking-wider transition-colors duration-200 ${
                  isActive ? "text-primary" : "text-muted-foreground"
                }`
              }
            >
              <n.icon className="h-5 w-5" />
              {n.label}
            </NavLink>
          ))}
        </nav>
      </div>
    </div>
  );
};

export const PageHeader = ({ title, subtitle, children }) => (
  <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 mb-8 stagger-in">
    <div>
      <p className="overline">{subtitle}</p>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tighter font-display mt-1">{title}</h1>
    </div>
    <div className="flex flex-wrap gap-2">{children}</div>
  </div>
);
